import React, { useCallback, useState } from "react";
import { Triangle } from "lucide-react";
import { useIntl } from "react-intl";
import Popover from "../../../../../../library/UI/Popover";
import { ZOOM_MENU_SPANS, spanToLabel } from "../../constants";
import "./ZoomMenu.sass";

const ARROW_SIZE = 8;

/**
 * Zoom button in the bottom left corner (po-chart-visual-spec.md §6): shows
 * the visible span (M21, H8…) and opens upwards a list of spans M10…D14.
 * As in PO, the visible span is in the list at its place and active, also
 * when it is not one of M10…D14.
 *
 * @param {object} props
 * @param {{ label: string, seconds: number }} props.span - visible span from useVisibleSpan
 * @param {(seconds: number) => void} props.onSelectSpan - shows this span up to the latest bars
 */
const ZoomMenu = ({ span, onSelectSpan }) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const label = intl.formatMessage({ id: "futures.chart.zoom.visible_range" });

  const presetItems = ZOOM_MENU_SPANS.map((seconds) => ({ seconds, label: spanToLabel(seconds) }));
  const isPreset = presetItems.some((item) => item.label === span.label);
  const items = isPreset
    ? presetItems
    : [...presetItems, span].sort((first, second) => first.seconds - second.seconds);

  const toggle = useCallback(() => setIsOpen((current) => !current), []);
  const close = useCallback(() => setIsOpen(false), []);

  const handleSelect = (seconds) => {
    onSelectSpan(seconds);
    close();
  };

  const trigger = (
    <button
      type="button"
      className={`zoom-menu__trigger${isOpen ? " zoom-menu__trigger--active" : ""}`}
      aria-label={`${label}: ${span.label}`}
      title={label}
      aria-expanded={isOpen}
      onClick={toggle}
    >
      {span.label}
      <Triangle
        className="zoom-menu__arrow"
        size={ARROW_SIZE}
        fill="currentColor"
        strokeWidth={0}
        aria-hidden="true"
      />
    </button>
  );

  return (
    <div className="zoom-menu">
      <Popover isOpen={isOpen} onClose={close} trigger={trigger} className="zoom-menu__panel">
        <ul className="zoom-menu__list">
          {items.map(({ seconds, label: itemLabel }) => {
            const isActive = itemLabel === span.label;

            return (
              <li key={itemLabel}>
                <button
                  type="button"
                  className={`zoom-menu__item${isActive ? " zoom-menu__item--active" : ""}`}
                  aria-pressed={isActive}
                  onClick={() => handleSelect(seconds)}
                >
                  {itemLabel}
                </button>
              </li>
            );
          })}
        </ul>
      </Popover>
    </div>
  );
};

export default ZoomMenu;
